import { showError } from '@calendly/alert'
import { useFlag } from '@calendly/feature-check-react'
import { I18n } from '@calendly/i18n'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import {
  useGetMeetingIntelligenceUserSettingsQuery,
  useGetEventsQuery,
  useLazyGetMoreEventsQuery,
} from 'features/meeting_intelligence/api'
import {
  getNewSelectedEventTypes,
  recordingRulesEventDataSelector,
  updateEventsObj,
  updateSelectedEvents,
} from 'features/meeting_intelligence/components/recording_rules/helpers'

export const NotetakerSettingsContext = createContext()

// PATCH
const gatherRecordingDefaultSettingsByOption = (
  dropdownOption,
  localIncludeList,
) => {
  // TODO: refactor setStartingState to not require an exclude list
  // This is currently necessary to accommodate users in a legacy rule config
  switch (dropdownOption) {
    case 'MANUAL':
      // "only when added manually from the Meetings page."
      return {
        autoRecordV3Calendly: false,
        autoRecordV3NonCalendly: false,
        autoRecordV3RequireExternalInvitees: false,
        autoRecordV3CalendlyIncludeList: [...localIncludeList],
        autoRecordV3CalendlyExcludeList: [],
        autoRecordV3NonCalendlyHostedMeetingsOnly: false
      }
    case 'OUTSIDE_COMPANY':
      // "meetings with people outside your company."
      return {
        autoRecordV3Calendly: true,
        autoRecordV3NonCalendly: true,
        autoRecordV3RequireExternalInvitees: true,
        autoRecordV3CalendlyIncludeList: [...localIncludeList],
        autoRecordV3CalendlyExcludeList: [],
        autoRecordV3NonCalendlyHostedMeetingsOnly: false
      }
    case 'VIA_CALENDLY':
      // "meetings scheduled via Calendly."
      // depends on event type include list
      return {
        autoRecordV3Calendly: true,
        autoRecordV3NonCalendly: false,
        autoRecordV3RequireExternalInvitees: false,
        autoRecordV3CalendlyIncludeList: [...localIncludeList],
        autoRecordV3CalendlyExcludeList: [],
        autoRecordV3NonCalendlyHostedMeetingsOnly: false
      }
    case 'VIA_CALENDAR':
      // "meetings scheduled via your connected calendar."
      // depends on valid calendar connection
      return {
        autoRecordV3Calendly: false,
        autoRecordV3NonCalendly: true,
        autoRecordV3RequireExternalInvitees: false,
        autoRecordV3CalendlyIncludeList: [],
        autoRecordV3CalendlyExcludeList: [],
        autoRecordV3NonCalendlyHostedMeetingsOnly: false
      }
    case 'ALL':
      // "all meetings"
      return {
        autoRecordV3Calendly: true,
        autoRecordV3NonCalendly: true,
        autoRecordV3RequireExternalInvitees: false,
        autoRecordV3CalendlyIncludeList: [...localIncludeList],
        autoRecordV3CalendlyExcludeList: [],
        autoRecordV3NonCalendlyHostedMeetingsOnly: false
      }
    default:
      return {}
  }
}

// Recording rules, recap sharing, and any other user settings that are handled by /update_user_settings calls
// GET: Sets "original settings" from the fetch API calls for recording rules and "auto share recap emails", which is separate
// Toggle behavior: Includes various states based on the dropdown options and auto share recap.
// PATCH: Sends "local settings" to the submit function in the user/submit_cancel_settings.js file
export const NotetakerSettingsProvider = ({
  children,
}) => {
  const dispatch = useDispatch()

  // Fetch user settings data
  const {
    data: userSettingsData,
    isLoading: isUserSettingsLoading,
  } = useGetMeetingIntelligenceUserSettingsQuery({})

  const { data: eventTypeData } = useGetEventsQuery({}, { refetchOnMountOrArgChange: true })
  const [getMoreEventsTrigger] = useLazyGetMoreEventsQuery()
  const automatedRecapSharingEnabled = useFlag('automated_recap_sharing')
  const notetakerNoticeEmailEnabled = useFlag('notetaker_notice_email')

  // State management - track local changes vs original values
  const [originalSettings, setOriginalSettings] = useState(null)
  const [autoShareRecapEmails, setAutoShareRecapEmails] = useState(false)
  const [preMeetingRecordingNotice, setPreMeetingRecordingNotice] = useState(false)
  const [recordingDefaultOption, setRecordingDefaultOption] = useState('ALL')
  const [eventTypes, setEventTypes] = useState([])
  const [hasEventError, setHasEventError] = useState(false)

  // Sets the starting state for recordingDefaultOption and scheduledFromNonCalendly based on originalSettings
  // GET
  const setStartingState = useCallback((settings) => {
    if (!settings) return

    const {
      autoRecordV3Calendly,
      autoRecordV3NonCalendly,
      autoRecordV3RequireExternalInvitees,
      autoRecordV3CalendlyExcludeList,
    } = settings

    const hasCalendlyExcludeList = (autoRecordV3CalendlyExcludeList?.length ?? 0) > 0

    // "only when added manually from the Meetings page."
    if (!autoRecordV3Calendly && !autoRecordV3NonCalendly) {
      setRecordingDefaultOption('MANUAL')

      // "meetings with people outside your company".
    } else if (autoRecordV3Calendly && autoRecordV3NonCalendly && autoRecordV3RequireExternalInvitees) {
      setRecordingDefaultOption('OUTSIDE_COMPANY')

      // "meetings scheduled via Calendly."
    } else if (autoRecordV3Calendly && !autoRecordV3NonCalendly) {
      setRecordingDefaultOption('VIA_CALENDLY')

      // "meetings scheduled via your connected calendar."
    } else if (!autoRecordV3Calendly && autoRecordV3NonCalendly) {
      setRecordingDefaultOption('VIA_CALENDAR')

      // Legacy HOW_SCHEDULED users: both flags true but with a selective exclude list.
      // Must be checked before ALL to avoid overwriting their event type choices on save.
      // Routes to ERROR to display a message instructing user to choose a valid option.
    } else if (autoRecordV3Calendly && autoRecordV3NonCalendly && hasCalendlyExcludeList) {
      setRecordingDefaultOption('ERROR')

      // "all meetings"
      // Note: we also show the "All Meetings" option if all event types selected.
    } else if (autoRecordV3Calendly && autoRecordV3NonCalendly) {
      setRecordingDefaultOption('ALL')

      // Technically unreachable: all combinations are handled above.
      // Kept as a defensive fallback: ERROR will display a banner and
      // allow user to select a new valid option from the dropdown
    } else {
      setRecordingDefaultOption('ERROR')
    }
  }, [])

  // Initialize settings from API data
  useEffect(() => {
    if (!userSettingsData) return

    // Store the original settings object for comparison
    setOriginalSettings(userSettingsData)

    // Initialize all state based on settings
    setStartingState(userSettingsData)
    setAutoShareRecapEmails(userSettingsData?.autoShareRecapEmails ?? false)
    setPreMeetingRecordingNotice(userSettingsData?.preMeetingRecordingNotice ?? false)
  }, [userSettingsData, setStartingState])

  // Initialize event types from event type API + user settings include list
  useEffect(() => {
    const collection = recordingRulesEventDataSelector(eventTypeData)
    const includeList = originalSettings?.autoRecordV3CalendlyIncludeList ?? []
    const updated = updateEventsObj(collection, includeList)
    setEventTypes(updated)
  }, [eventTypeData, originalSettings?.autoRecordV3CalendlyIncludeList])

  const handleEventTypeSelect = useCallback((eventTypeId, itemId, isChecked) => {
    setEventTypes((prev) => prev.map((eventType) => {
      if (String(eventType.id) === String(eventTypeId)) {
        const newItems = eventType.items.map((item) => {
          if (item.id === itemId) {
            return { ...item, selected: isChecked }
          }
          return item
        })
        return { ...eventType, items: newItems }
      }
      return eventType
    }))
    setHasEventError(false)
  }, [])

  const handleSetAllEventTypes = useCallback((selected) => {
    setEventTypes((prev) => prev.map((eventV) => ({
      ...eventV,
      items: eventV.items.map((item) => ({ ...item, selected }))
    })))
    setHasEventError(false)
  }, [])

  const handleFetchMoreEvents = useCallback(
    async (groupData) => {
      const { id: groupId, type: groupType, currentPage = 1 } = groupData
      const nextPage = currentPage + 1
      const { data: moreData, isSuccess } = await getMoreEventsTrigger({
        groupId,
        groupType,
        page: nextPage
      })
      if (isSuccess && moreData) {
        const collection = moreData?.collection ?? moreData?.groups?.collection ?? []
        const pagination = moreData?.pagination ?? {}
        const currentPageNum = pagination?.currentPage ?? pagination?.current_page
        const includeList = originalSettings?.autoRecordV3CalendlyIncludeList ?? []
        const updatedCollection = updateSelectedEvents(collection, includeList)
        setEventTypes((prev) => prev.map((evTSection) => {
          if (evTSection.id === groupData.id) {
            return {
              ...evTSection,
              items: [...evTSection.items, ...updatedCollection],
              currentPage: currentPageNum,
              showButton: collection.length > 0,
            }
          }
          return evTSection
        }))
      }
      return { moreData, moreDataLoading: false, isSuccess }
    },
    [getMoreEventsTrigger, originalSettings?.autoRecordV3CalendlyIncludeList]
  )

  const actualSelectedCount = useMemo(() => {
    const oldSet = new Set(originalSettings?.autoRecordV3CalendlyIncludeList ?? [])
    return getNewSelectedEventTypes(oldSet, eventTypes).size
  }, [originalSettings?.autoRecordV3CalendlyIncludeList, eventTypes])

  // Compare current state with original settings to find changed fields.
  // Returns { payload, valid }. valid is false when rule is VIA_CALENDLY but no event types selected.
  const handleGatherUserSettings = useCallback(() => {
    const effectiveOption = recordingDefaultOption

    if (effectiveOption === 'ERROR') {
      return { payload: {}, valid: false }
    }

    const originalInclude = originalSettings?.autoRecordV3CalendlyIncludeList ?? []

    // Pull all eventTypeIds for ALL and OUTSIDE_COMPANY settings
    const allEventTypeIds = eventTypes.flatMap(
      (group) => (group.items ?? []).map((item) => item.id)
    )

    let includeList
    if (effectiveOption === 'VIA_CALENDLY') {
      includeList = [...getNewSelectedEventTypes(new Set(originalInclude), eventTypes)]
    } else if (effectiveOption === 'OUTSIDE_COMPANY' || effectiveOption === 'ALL') {
      includeList = allEventTypeIds
    } else if (effectiveOption === 'MANUAL' || effectiveOption === 'VIA_CALENDAR') {
      includeList = []
    } else { // defensive catch if there's an error
      includeList = originalInclude
    }
    const eventTypeSelectionInvalid = (effectiveOption === 'VIA_CALENDLY'
      && includeList.length === 0)

    if (eventTypeSelectionInvalid) {
      dispatch(showError(I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.error_toast')))
      setHasEventError(true)
    }

    const recordingDefaultSettings = gatherRecordingDefaultSettingsByOption(
      effectiveOption,
      includeList,
    )
    const payload = {
      ...(automatedRecapSharingEnabled && { autoShareRecapEmails }),
      ...(notetakerNoticeEmailEnabled && { preMeetingRecordingNotice }),
      ...recordingDefaultSettings,
    }
    return { payload, valid: !eventTypeSelectionInvalid }
  }, [
    autoShareRecapEmails,
    automatedRecapSharingEnabled,
    preMeetingRecordingNotice,
    notetakerNoticeEmailEnabled,
    recordingDefaultOption,
    originalSettings?.autoRecordV3CalendlyIncludeList,
    eventTypes,
    dispatch,
  ])

  // Reset ALL settings to original values
  const handleCancelUserSettings = useCallback(() => {
    if (originalSettings) {
      setAutoShareRecapEmails(originalSettings?.autoShareRecapEmails ?? false)
      setPreMeetingRecordingNotice(originalSettings?.preMeetingRecordingNotice ?? false)
      setStartingState(originalSettings)
      setHasEventError(false)
      const collection = recordingRulesEventDataSelector(eventTypeData)
      const includeList = originalSettings?.autoRecordV3CalendlyIncludeList ?? []
      setEventTypes(updateEventsObj(collection, includeList))
    }
  }, [originalSettings, setStartingState, eventTypeData])

  const contextValue = useMemo(
    () => ({
      autoShareRecapEmails,
      setAutoShareRecapEmails,
      preMeetingRecordingNotice,
      setPreMeetingRecordingNotice,
      recordingDefaultOption,
      setRecordingDefaultOption,
      eventTypes,
      hasEventError,
      actualSelectedCount,
      handleEventTypeSelect,
      handleSelectAll: () => handleSetAllEventTypes(true),
      handleClearAll: () => handleSetAllEventTypes(false),
      handleFetchMoreEvents,
      isLoading: isUserSettingsLoading,
      handleGatherUserSettings,
      handleCancelUserSettings,
    }),
    [
      autoShareRecapEmails,
      setAutoShareRecapEmails,
      preMeetingRecordingNotice,
      setPreMeetingRecordingNotice,
      recordingDefaultOption,
      setRecordingDefaultOption,
      eventTypes,
      hasEventError,
      actualSelectedCount,
      handleEventTypeSelect,
      handleSetAllEventTypes,
      handleFetchMoreEvents,
      isUserSettingsLoading,
      handleGatherUserSettings,
      handleCancelUserSettings,
    ]
  )

  return (
    <NotetakerSettingsContext.Provider value={contextValue}>
      {children}
    </NotetakerSettingsContext.Provider>
  )
}

export const useNotetakerSettingsContext = () => useContext(NotetakerSettingsContext)
